import { useCallback, useMemo } from 'react';

import { useCartStorage } from '#hooks/useCartStorage.js';
import { useShopRoutes } from '#routes/useShopRoutes.js';
import { useCreateConsumerCheckout } from '@givebacks/api/store';
import { getErrorMessage } from '@givebacks/utils';
import { Button, notifications } from 'ui';

type GoToCheckoutButtonProps = {
  /** If a cartId is provided, it will be used to navigate to the checkout page for that cart*/
  cartId?: string;
  /** Callback called when the checkout button is clicked before the checkout is created */
  onCheckoutClick?: () => void;
};

/**
 * A button that allows the user to go to the checkout page if there are carts in the shop.
 * If a cartId is provided, it will be used to navigate to the checkout page for that cart,
 * otherwise the consolidated checkout experience will be initiated
 */
const GoToCheckoutButton = ({ cartId, onCheckoutClick }: GoToCheckoutButtonProps) => {
  const { cartCount, cartsGroupedByStore } = useCartStorage();
  const { navigateToCheckout } = useShopRoutes();
  const { mutate: createConsumerCheckoutCart, isPending: isLoading } = useCreateConsumerCheckout();

  const pendingCartIds = useMemo(() => {
    if (cartId) {
      return [cartId];
    }
    return cartsGroupedByStore.map((cart) => cart.cart.cartId);
  }, [cartId, cartsGroupedByStore]);

  const handleGoToCheckout = useCallback(() => {
    createConsumerCheckoutCart(
      {
        checkout: {
          pending_cart_ids: pendingCartIds,
        },
      },
      {
        onSuccess: (response) => {
          navigateToCheckout(response.checkout.uuid);
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage(error, 'Error creating cart.'),
            variant: 'error',
          });
        },
        onSettled: () => {
          onCheckoutClick?.();
        },
      }
    );
  }, [createConsumerCheckoutCart, navigateToCheckout, onCheckoutClick, pendingCartIds]);

  if (!cartCount || cartCount === 0) {
    return null;
  }

  // TODO: Use a hook to fetch all cart information before allowing the Checkout to be clicked
  return (
    <Button w="100%" onClick={handleGoToCheckout} loading={isLoading}>
      Go to Checkout
    </Button>
  );
};

export { GoToCheckoutButton };
